import { Route } from '@angular/router';
import {
  authGuard,
  CampaignMapRedirectionResolver,
  CampaignRedirectionResolver,
  CommunityFullviewResolver,
  CommunityUserGuard,
  CommunityUserResolver,
  CustomTemplatePermissionGuard,
  PlatformService,
  StorytellerResolver,
  canDeactivateGuard,
  InitiativeMetaResolver,
  CommunityMetaResolver,
} from '@sml/utility';
import { AuthSignupComponent } from './components/auth-signup/auth-signup.component';
import { AuthLoginComponent } from './components/auth-login/auth-login.component';
import { CommunityFullViewComponent } from './components/community-fullview.component';
import { from, map, switchMap } from 'rxjs';
import { inject, Injector } from '@angular/core';


export const auth0User = () => {
  const injector = inject(Injector);
  const platformService = inject(PlatformService);

  if (!platformService.browser()) return true;

  return from(import('@auth0/auth0-angular'))
  .pipe(
    map(module => module.AuthService),
    switchMap((AuthService) => {
      const service = injector.get(AuthService);
      return service.user$;
    })
  );
};

/**
 * Children of `campaign/:campaignId` that Campaign Overview v2 has not rebuilt
 * yet, and so still resolve to the previous screen under `campaign-v1`.
 *
 * Needed because the v1 components navigate with hardcoded
 * `/community/x/campaign/y/map` style paths: without these redirects they would
 * jump out of the v1 branch into v2 mid-flow, and existing deep links would
 * land on a route that no longer exists.
 */
const CAMPAIGN_V1_HANDOFF_PATHS = [
  'map',
  'gallery',
  'private',
  'activity-log',
  'qr-code-scan',
] as const;

export const appRoutes: Route[] = [
  {
    path: '',
    pathMatch: 'full',
    resolve: {
      auth0User: auth0User,
    },
    loadComponent: () =>
      import(`./components/landing/landing.component`).then(
        (m) => m.LandingComponent
      ),
  },
  
  {
    path: 'callback',
    loadComponent: () =>
      import(`./components/callback/callback.component`).then(
        (m) => m.CallbackComponent
      ),
    canActivate: [authGuard],
  },
  {
          path: 'invalid-user',
          loadComponent: () =>
            import(`./components/error-page/error-page.component`).then(
              (m) => m.ErrorPageComponent
            ),
        },

   {
    path: 'expired',
    loadComponent: () =>
      import(`./components/subscription-landing/subscription-landing.component`).then(
        (m) => m.SubscriptionLandingComponent
      ),
  },
  {
    path: 'application-payment/:applicationId',
    loadComponent: () =>
      import('@sml/survey-js').then((m) => m.ApplicationPaymentComponent),
  },
  {
    path: 'community/:id',
    resolve: {
      isCommunityUser: CommunityUserResolver,
    },
    loadComponent: () =>
      import(`./components/layout/layout.component`).then(
        (m) => m.LayoutComponent
      ),
    children: [
      //#region Public + Community User
      {
        path: '',
        pathMatch: 'full',
        resolve: {
          communityMeta: CommunityMetaResolver
        },
        loadComponent: () =>
          import(`@sml/community-detail`).then(
            (m) => m.CommunityDetailComponent
          ),
        data: { showNavigation: true },
      },
      {
        path: 'stories',
        loadComponent: () =>
          import('@sml/community-story').then((c) => c.CommunityStoryComponent),
        data: { showNavigation: true },
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import(`@sml/campaign-overview`).then(
                (m) => m.CampaignMapViewComponent
              ),
          },
          {
            path: 'gallery',
            loadComponent: () =>
              import(`@sml/campaign-overview`).then(
                (m) => m.CampaignStoriesGalleryComponent
              ),
          },
        ],
      },
      {
        path: 'campaigns',
        loadComponent: () =>
          import('@sml/campaigns').then((c) => c.CampaignsComponent),
        data: {
          showNavigation: true
        },
      },
      {
        path: 'campaign-options',
        loadComponent: () =>
          import('./components/campaign-options/campaign-options.component').then((m) => m.CampaignOptionsComponent),
      },
       {
        path: 'ai-campaign-setup',
        loadComponent: () =>
          import('@sml/ai-campaign-setup').then((m) => m.CampaignSetupShellComponent),
      },
      {
        path: 'initiatives',
        loadComponent: () =>
          import('@sml/community-initiatives').then((c) => c.CommunityInitiativeComponent),
        data: {
          showNavigation: true
        },
      },
      {
        path: 'initiative/:initiativeId',
        loadComponent: () =>
          import('@sml/community-initiatives').then((c) => c.InitiativeOverviewComponent),
        resolve: {
          initiativeMeta: InitiativeMetaResolver
        },
        data: {
          showNavigation: false
        },
      },
      // Campaign Overview v2 — the redesigned screen, now serving the primary
      // `campaign/:campaignId` path so campaign cards land on it. The previous
      // screen is unchanged and still complete at `campaign-v1/:campaignId`.
      //
      // This swap is the temporary stand-in for the Phase 5 feature flag. To
      // put the old screen back: give the branch below `campaign/:campaignId`
      // again and delete these two entries. See docs/plans/001-campaign-overview/.
      //
      // v2 only implements `overview` so far, so every sibling screen redirects
      // into the v1 branch. These MUST stay ahead of the v2 entry — Angular
      // matches in order, and `campaign/:campaignId` would otherwise swallow
      // them. Each is deleted as its v2 replacement lands (`map` in Phase 4).
      ...CAMPAIGN_V1_HANDOFF_PATHS.map(
        (child): Route => ({
          path: `campaign/:campaignId/${child}`,
          redirectTo: `campaign-v1/:campaignId/${child}`,
        })
      ),
      {
        path: 'campaign/:campaignId',
        loadChildren: () =>
          import('@sml/campaign-overview-v2').then(
            (m) => m.campaignOverviewV2Routes
          ),
        data: { showNavigation: false },
      },
      {
        path: 'campaign-v1/:campaignId',
        loadComponent: () =>
          import(`@sml/campaign-overview`).then(
            (m) => m.ManageCampaignComponent
          ),
        children: [
          { path: '', pathMatch: 'full', redirectTo: 'overview' },
          {
            path: 'overview',
            loadComponent: () =>
              import(`@sml/campaign-overview`).then(
                (m) => m.CampaignOverviewComponent
              ),
              resolve: {isCampaignRedirection: CampaignRedirectionResolver},
          },
          {
            path: 'map',
            loadComponent: () =>
              import(`@sml/campaign-overview`).then(
                (m) => m.CampaignMapViewComponent
              ),
              resolve: {isCampaignMapRedirection: CampaignMapRedirectionResolver},
          },
            
          {
            path: 'gallery',
            loadComponent: () =>
              import(`@sml/campaign-overview`).then(
                (m) => m.CampaignGalleryComponent
              ),
              data: { animation: 'GalleryPage' }
          },
          {
            path: 'private',
            loadComponent: () =>
              import(`@sml/campaign-overview`).then(
                (m) => m.CampaignOverviewComponent
              ),
          },
         
          
          {
            path: 'activity-log',
            loadComponent: () =>
              import(`@sml/activity-log`).then((m) => m.ActivityLogComponent),
            data: { hideCampaignOverviewNav: true },
            canActivate: [authGuard],
          },
          {
            path: 'qr-code-scan',
            loadComponent: () =>
              import(`@sml/activity-check-in-manager`).then((m) => m.ActivityCheckInManagerComponent),
            data: { hideCampaignOverviewNav: true },
            canActivate: [authGuard],
          },
        ],
      },
      //#endregion

      //#region Community User Only
      {
        path: 'stories/form',
        canActivate: [CommunityUserGuard],
        loadComponent: () =>
          import('@sml/community-detail').then(
            (c) => c.CommunityStoryFormComponent
          ),
        data: { showNavigation: false, accessFor: 'community' },
      },
      {
        path: 'activity-log',
        canActivate: [CommunityUserGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import(`@sml/activity-log`).then((m) => m.ActivityLogComponent),
          },
          {
            path: 'qr-code-scan',
            loadComponent: () =>
              import(`@sml/activity-check-in-manager`).then((m) => m.ActivityCheckInManagerComponent),
            data: { hideCampaignOverviewNav: true },
          },
        ],
      },
      {
        path: 'edit',
        loadComponent: () =>
          import(`@sml/edit-profile`).then((m) => m.EditProfileComponent),
        canActivate: [CommunityUserGuard],
      },
      {
        path: 'initiatives/create',
        loadComponent: () =>
          import('@sml/community-initiatives/build-initiative').then((m) => m.BuildInitiativeComponent),
        canActivate: [CommunityUserGuard],
        canDeactivate: [canDeactivateGuard],
      },
     
      {
        path: 'create',
        loadComponent: () =>
          import(`@sml/build-campaign`).then((m) => m.BuildCampaignComponent),
        canActivate: [CommunityUserGuard],
        canDeactivate: [canDeactivateGuard],
      },
      {
        path: 'users',
        loadChildren: () =>
          import(`@sml/manage-users`).then((m) => m.ManageUsersModule),
        canActivate: [CommunityUserGuard],
      },
      {
        path: 'subscription',
        loadComponent: () =>
          import(`@sml/subscription`).then((m) => m.SubscriptionComponent),
        canActivate: [CommunityUserGuard],
      },
      {
        path: 'disbursements',
        canActivate: [CommunityUserGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import(`@sml/disbursements`).then((m) => m.DisbursementsComponent),
          },
          {
            path: 'accounts',
            loadComponent: () =>
              import(`@sml/disbursements`).then(
                (m) => m.DisbursementAccountsComponent
              ),
          },
        ],
      },
      {
        path: 'custom-grid',
        loadComponent: () =>
          import(`@sml/custom-grid`).then((m) => m.CustomGridComponent),
        canActivate: [CommunityUserGuard],
      },
      {
        path: 'custom-template-grid',
        loadComponent: () =>
          import(`@sml/custom-grid`).then((m) => m.CustomGridComponent),
        canActivate: [CommunityUserGuard, CustomTemplatePermissionGuard],
      },
      {
        path: 'custom-template-form-builder',
        loadComponent: () =>
          import(`@sml/form-builder`).then((m) => m.FormBuilderComponent),
        canActivate: [CommunityUserGuard, CustomTemplatePermissionGuard],
      },
      {
        path: 'custom-form-builder',
        loadComponent: () =>
          import(`@sml/form-builder`).then((m) => m.FormBuilderComponent),
        canActivate: [CommunityUserGuard],
      },
      {
        path: 'create-form',
        loadComponent: () =>
          import(`@sml/survey-js`).then((m) => m.SurveyBuilderComponent),
      },

      {
        path: 'plaques',
        canActivate: [CommunityUserGuard],
        children: [
          {
            path: '',
            loadComponent: () =>
              import(`@sml/plaques`).then((m) => m.PlaquesComponent),
          },
          {
            path: 'activate/:plaqueId/:typeId',
            loadComponent: () =>
              import(`@sml/plaques`).then((m) => m.ActivatePlaquesComponent),
          },
          {
            path: 'customize/:plaqueId',
            loadComponent: () =>
              import(`@sml/plaques`).then((m) => m.CustomizePlaqueComponent),
          },
        ],
      },
      {
        path: 'recordPayment/:itemId',
        loadComponent: () =>
          import(`@sml/record-payment`).then(
            (m) => m.RecordPaymentComponent
          ),
          canActivate: [CommunityUserGuard],
      },
      {
        path: 'item/update',
        canActivate: [CommunityUserGuard],
        loadComponent: () =>
          import(`@sml/update-item`).then((m) => m.UpdateItemComponent),
      },
      {
        path: 'item/add',
        canActivate: [CommunityUserGuard],
        loadComponent: () =>
          import(`@sml/update-item`).then((m) => m.UpdateItemComponent),
      },
      //#endregion
    ],
  },

  {
    path: 'storyteller',
    resolve: {
      isStoryteller: StorytellerResolver,
    },
    canActivate: [authGuard],
    loadComponent: () =>
      import(`./components/story-layout/story-layout.component`).then(
        (m) => m.StoryLayoutComponent
      ),
    children: [
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () =>
          import(
            `./components/story-layout/storyteller-home/storyteller-home.component`
          ).then((m) => m.StorytellerHomeComponent),
      },
      {
        path: 'customize/:plaqueId',
        loadComponent: () =>
          import(`@sml/plaques`).then((m) => m.CustomizePlaqueComponent),
        data: { accessFor: 'storyteller' },
      },
      {
        path: ':id/stories/form',
        loadComponent: () =>
          import(`@sml/community-detail`).then(
            (c) => c.CommunityStoryFormComponent
          ),
        data: { accessFor: 'storyteller' },
      },
    ],
  },
  {
    path: 'community-access',
    canActivate: [authGuard],
    loadComponent: () =>
      import(`@sml/community-access`).then((c) => c.CommunityAccessComponent),
  },
  {
    path: 'preview',
    loadComponent: () =>
      import(`@sml/share-story`).then((c) => c.ShareStoryComponent),
  },
  { path: 'Login/Login', redirectTo: "Login" },
  { path: 'login/login', redirectTo: "Login" },
  { path: 'Login/login', redirectTo: "Login" },
  { path: 'login/Login', redirectTo: "Login" },
  { path: 'login', redirectTo: "Login" },
  { 
    path: 'Login', component: AuthLoginComponent, 
    resolve: {
      auth0User: auth0User,
    }, 
  },
  { 
    path: 'signup', 
    component: AuthSignupComponent,
    resolve: {
      auth0User: auth0User,
    }, 
  },

  {
    path: 'CampaignFullView',
    component: CommunityFullViewComponent,
    resolve: { isCommunityUser: CommunityFullviewResolver },
  },
  {
    path: 'communityfullview/:communityId',
    component: CommunityFullViewComponent,
    resolve: { isCommunityUser: CommunityFullviewResolver },
  },
];
